"use client";

import { ChoiceBox } from "../ChoiceBox";
import { OrderSummaryCard } from "../OrderSummaryCard";
import { SheenRing } from "../SheenRing";
import type { PackageSummary } from "../pricing";

/**
 * Both "do you need help with previous years?" screens offer exactly these two
 * answers, word for word.
 *
 * The exports read "Sleek's help" — a competitor's name. Ours instead.
 */
export const YES_NO_OPTIONS = [
  {
    id: "yes",
    label: "Yes, I need MuteTaxes’ help to prepare these documents for previous years",
  },
  { id: "no", label: "No, I have this covered" },
] as const;

/**
 * The manage branch's past-years question: a heading, an intro, an optional
 * explainer panel, and the two answer cards.
 *
 * Shared because the company-secretary and accounting versions of this screen
 * are identical apart from their copy and that explainer.
 */
export function ManageYesNoStep({
  heading,
  intro,
  name,
  value,
  onChange,
  summary,
  onNext,
  children,
}: {
  heading: string;
  /** Rendered as-is, so a caller can keep the design's line break. */
  intro: React.ReactNode;
  /** Radio group name — must differ per screen or the two would share state. */
  name: string;
  value: string | null;
  onChange: (value: string) => void;
  summary: PackageSummary;
  onNext: () => void;
  /** Sits between the intro and the answer cards. */
  children?: React.ReactNode;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (value) onNext();
      }}
      className="mx-auto w-full max-w-[1250px] px-4 pb-16 pt-8 sm:px-6 sm:pb-24 sm:pt-12 lg:pt-[31px]"
    >
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
        {/* items-end only from lg — below that it would push the Next button
            right of everything else. */}
        <div className="flex min-w-0 flex-1 flex-col items-start gap-6 sm:gap-8 lg:items-end">
          <h1 className="w-full font-suisse-intl-bold text-[26px] leading-tight text-white sm:text-4xl md:text-5xl lg:text-[53px] lg:leading-[71px]">
            {heading}
          </h1>

          <p className="w-full font-suisse-intl-medium text-base leading-relaxed text-white sm:text-lg sm:leading-[28.8px]">
            {intro}
          </p>

          {children}

          {/* Side by side only from lg: the first label is a full sentence, and
              two columns at 640px leave ~230px each. */}
          <fieldset className="flex w-full flex-col gap-6 sm:gap-8 lg:flex-row lg:items-stretch">
            <legend className="sr-only">{heading}</legend>
            {YES_NO_OPTIONS.map((option) => {
              const selected = value === option.id;

              return (
                // Same gradient ring as the banking cards: the border is the
                // label's own background showing through a 1px padding gap
                // around the opaque inner panel, with the conic sheen sitting
                // in that gap.
                <label
                  key={option.id}
                  className="group relative flex flex-1 cursor-pointer overflow-hidden rounded-[28px] bg-white/20 p-px sm:rounded-[32px]"
                >
                  <SheenRing selected={selected} />

                  <input
                    type="radio"
                    name={name}
                    value={option.id}
                    checked={selected}
                    onChange={() => onChange(option.id)}
                    className="peer sr-only"
                  />

                  <span className="relative flex w-full items-start gap-2 rounded-[27px] bg-[#0F1319] p-6 peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-white/60 sm:rounded-[31px] sm:p-8">
                    <ChoiceBox selected={selected} />
                    <span className="flex-1 font-suisse-intl text-base leading-7 text-white sm:text-lg sm:leading-[28.8px]">
                      {option.label}
                    </span>
                  </span>
                </label>
              );
            })}
          </fieldset>

          {/* Full width on phones; the design's 388px right-aligned pill from
              lg, where items-end applies. */}
          <button
            type="submit"
            disabled={!value}
            className="flex h-14 w-full items-center justify-center rounded-full bg-white px-4 font-suisse-intl text-xl leading-5 text-[#2A4775] transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50 lg:max-w-[388px]"
          >
            Next
          </button>
        </div>

        <OrderSummaryCard summary={summary} className="lg:w-[402px] lg:shrink-0" />
      </div>
    </form>
  );
}
